import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { FilterPartyNames } from 'components/Dashboard/Analytics/ui/AnalyticsFilters/PartyMetadata';
import { useContext } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { store } from 'components/Store';
import { chartDataOptions } from './createAnalyticsState';
import { useLocation } from 'react-router';
import dayjs from 'dayjs';
import useSelectedCountriesFilters from './useSelectedCountriesFilters';
import { useChartDataOptionSelected } from './ui/MentionsByTopic/ChartDataTable';
import ChangeSearchQuery from 'components/Dashboard/utilities/changeSearchQuery';
import getAppSite from 'utils/getAppSite';
import utc from 'dayjs/plugin/utc';
import graphDataSections from 'components/Dashboard/Analytics/graphDataSections';
dayjs.extend(utc);

const useRightPanelIndividualCall = (props) => {
  const { analyticsState, contentState } = props;
  const { dataKey, chartDataKeys, date, region, totalRow } = contentState;

  const { currentView, allowFrontbenchers } = analyticsState;
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const list = keywordsLists.find((item) => item.id === parseInt(getUrlParam('topic-id')));

  const location = useLocation();
  const InterestByRegionSection = location.pathname.includes('/analytics/interest-by-region');
  const { chartOptionName: chartDataOptionNameState } = useChartDataOptionSelected(analyticsState);
  const chartOptionName = InterestByRegionSection ? 'Region' : chartDataOptionNameState;

  const totalCell = dataKey === 'totalCell';
  const contributionGraph = chartOptionName === 'Contribution type' || chartOptionName === 'Overall';
  const generalPageSize = parseInt(import.meta.env.VITE_APP_PAGE_SIZE);
  const parliamentaryPositionsFilters =
    chartDataOptions.find((item) => item.name === 'Parliamentary position')?.filters ?? [];

  const { generalApiCall } = useGeneralApiCall();
  const { getCountrySpecificFilters, UKSelected, WalesSelected } = useSelectedCountriesFilters();

  // Helper function to remove duplicate filters
  const deduplicateFilters = (filters) => {
    const seen = new Map();
    return filters.filter((filter) => {
      const key = `${filter.field}:${filter.value}:${filter.operator}`;
      if (seen.has(key)) {
        return false;
      }
      seen.set(key, true);
      return true;
    });
  };

  const sectionContentTypeValues = ({ type }) => {
    // Written questions are queried by questionByContactId (the MP who asked it) rather than
    // contactIds, which also matches answering ministers and returns the wrong sentiment mix.
    // graphDataSections.js carries one section per nation (e.g. "Written questions (Wales)"),
    // so its entry resolves both the backend type and the country filter for those sections.
    const sectionItem = graphDataSections.find((item) => item.name === type);
    const resolvedType = sectionItem?.type ?? type;
    const isWrittenQuestion = resolvedType === 'WrittenQuestion' || resolvedType === 'CountrySpecificWrittenQuestion';
    let typeToCall;
    let contentTypeField;
    if (isWrittenQuestion) {
      typeToCall = resolvedType;
      contentTypeField = 'questionByContactId';
    } else if (type?.toLowerCase()?.includes('tweet')) {
      typeToCall = 'tweet';
      contentTypeField = 'contactId';
    } else {
      typeToCall = 'ParliamentaryRecord';
      contentTypeField = 'contactIds';
    }
    return { typeToCall, contentTypeField, isWrittenQuestion, sectionItem };
  };

  const individualApiCall = async ({
    type,
    sentiment,
    pageNumber,
    snapshot,
    source,
    freeSearch,
    stakeholderId,
    pageSize,
    dateFilters,
    country,
  }) => {
    const pathname = `/api/search/${freeSearch ? 'query' : 'query-by-keyword-list'}`;
    const method = 'post';
    const { typeToCall, contentTypeField, isWrittenQuestion, sectionItem } = sectionContentTypeValues({ type });

    if ((type === 'Edm' && !UKSelected) || (type === 'SeneddCommitteeTranscriptContribution' && !WalesSelected)) {
      return {};
    }

    let requestProperties = {
      pageSize: pageSize ?? generalPageSize,
      pageNumber: pageNumber ?? 1,
      type: typeToCall,
      includeHighlights: true,
      sort: { field: 'dateTime', order: 'DESC' },
    };

    if (freeSearch) {
      requestProperties = {
        ...requestProperties,
        query: ChangeSearchQuery(decodeUrlQueryValue(freeSearch)),
      };
    } else {
      requestProperties = {
        ...requestProperties,
        keywordListId: list?.id,
      };
    }

    let chartDataOption = chartDataOptions.find((option) => option.name === chartOptionName);
    let filters = [];

    if (type === 'Committee contributions') {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'SeneddCommitteeTranscriptContribution',
          operator: 'str_eq',
        },
      ];
    }

    if (sentiment) {
      filters.push({
        field: 'sentiment',
        value: sentiment,
        operator: 'str_eq',
      });
    }

    if (stakeholderId) {
      filters = [
        ...filters,
        {
          field: contentTypeField,
          value: stakeholderId,
          operator: 'str_eq',
        },
      ];
    }

    if (isWrittenQuestion && country) {
      filters.push({ field: 'country', value: country, operator: 'str_eq' });
    }

    if (sectionItem?.additionalFilters) {
      filters = [...filters, ...sectionItem.additionalFilters];
    }

    if (getAppSite() === 'global' && sectionItem) {
      filters = [...filters, ...getCountrySpecificFilters({ value: sectionItem?.value, field: sectionItem?.field })];
    }
    if (getAppSite() === 'usa' && typeToCall.toLowerCase() !== 'tweet') {
      // Only add CREC filter for non-tweet types on US site
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'CREC',
          operator: 'str_eq',
        },
      ];
    }
    if (totalCell && !InterestByRegionSection) {
      filters = [...filters, ...parliamentaryPositionsFilters];
      if ((chartOptionName === 'Party' || chartOptionName === 'Region') && !!chartDataKeys) {
        chartDataKeys.forEach((key) => {
          let partyOption = chartOptionName === 'Party';
          filters.push({
            field: partyOption ? 'contactParty' : 'contactRegion',
            value: partyOption && FilterPartyNames[key] ? FilterPartyNames[key] : key,
            operator: 'str_eq',
          });
        });
      }
    }
    if (totalCell && InterestByRegionSection) {
      if (!!chartDataKeys) {
        if (totalRow) {
          const value = region === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : region;
          filters.push({
            field: 'contactRegion',
            value: value,
            operator: 'str_eq',
          });
        } else {
          chartDataKeys.forEach((key) => {
            const region = key === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : key;
            filters.push({
              field: 'contactRegion',
              value: region,
              operator: 'str_eq',
            });
          });
        }
      }
    }

    if (typeToCall.toLowerCase() === 'tweet' && sectionItem?.field === 'isRetweet') {
      filters.push({
        field: 'isRetweet',
        value: sectionItem.value,
        operator: 'str_eq',
      });
    }

    if (!totalCell && !contributionGraph && !stakeholderId) {
      const changeDataKey = () => {
        let key = dataKey;
        if (chartOptionName === 'Party' && FilterPartyNames[dataKey]) {
          key = FilterPartyNames[dataKey];
        }
        if (chartOptionName === 'Parliamentary position') {
          if (dataKey === 'Backbenchers') {
            key = 'backbencher';
          } else if (dataKey === 'MSs') {
            key = 'ms';
          } else if (dataKey === 'MSPs') {
            key = 'msp';
          } else {
            key = dataKey.toLowerCase();
          }
        }

        if (chartOptionName === 'Region' && dataKey === 'Yorkshire and the Humber') {
          key = 'yorkshire & the humber';
        }

        if (InterestByRegionSection) {
          key = region === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : region;
        }
        return key;
      };
      filters.push({
        field: `${chartDataOption.bucketField}`,
        value: changeDataKey(),
        operator: 'str_eq',
      });
    }

    if (InterestByRegionSection) {
      if (!allowFrontbenchers) {
        filters = [
          ...filters,
          {
            field: 'contactPositionCategories',
            value: 'Backbencher',
            operator: 'str_eq',
          },
          {
            field: 'contactPositionCategories',
            value: 'MSP',
            operator: 'str_eq',
          },
          {
            field: 'contactPositionCategories',
            value: 'MS',
            operator: 'str_eq',
          },
        ];
      } else {
        filters.push({
          field: 'contactCategories',
          value: 'MP',
          operator: 'str_eq',
        });
      }
    } else if (!totalCell && chartOptionName !== 'Parliamentary position' && !stakeholderId) {
      filters = [...filters, ...parliamentaryPositionsFilters];
    }

    //DATE FILTER
    if (snapshot) {
      if (chartOptionName !== 'Parliamentary position') {
        filters = [...filters, ...parliamentaryPositionsFilters];
      }
      if (dateFilters) {
        filters = [...filters, ...dateFilters];
      } else {
        filters.push(
          {
            field: 'dateTime',
            value: `${dayjs()
              .subtract(import.meta.env.VITE_APP_SENTIMENT_SNAPSHOT_NUM_DAYS ?? 90, 'day')
              .startOf('day')
              .format('YYYY-MM-DD')}`,
            operator: 'DATE_GTE',
          },
          {
            field: 'dateTime',
            value: `${dayjs().format('YYYY-MM-DD')}`,
            operator: 'DATE_LT',
          }
        );
      }
    } else if (InterestByRegionSection) {
      filters.push(
        {
          field: 'dateTime',
          value: dayjs().subtract(365, 'day').add(-1, 'day').startOf('day').format('YYYY-MM-DD'),
          operator: 'date_gte',
        },
        {
          field: 'dateTime',
          value: dayjs().add('1', 'day').format('YYYY-MM-DD'),
          operator: 'date_lt',
        }
      );
    } else {
      let nextDate = dayjs(date).startOf('day').add('1', currentView.toLowerCase()).format('YYYY-MM-DD');
      filters.push(
        {
          field: 'dateTime',
          value: `${dayjs(date).startOf('day').format('YYYY-MM-DD')}`,
          operator: 'DATE_GTE',
        },
        { field: 'dateTime', value: `${nextDate}`, operator: 'DATE_LT' }
      );
    }
    // Remove duplicate filters before making the API call
    filters = deduplicateFilters(filters);

    requestProperties = {
      ...requestProperties,
      filters,
    };

    return await generalApiCall({
      pathname,
      method,
      requestProperties,
      requestSource: source,
      notShowErrorMessage: true,
      bigInt: true,
      needsAuthentication: true,
    });
  };

  return { individualApiCall };
};

export default useRightPanelIndividualCall;
